import { useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Phone, Mail, MapPin, Clock, Send, CheckCircle2 } from "lucide-react";
import { COMPANY } from "@/lib/index";

const springGentle = { type: "spring" as const, stiffness: 300, damping: 35 };

export default function ContactSection() {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Compose mailto link with form data
    const body = `Name: ${form.name}%0ATelefon: ${form.phone}%0ANachricht: ${form.message}`;
    window.location.href = `mailto:${COMPANY.email}?subject=${encodeURIComponent(form.subject || "Anfrage von KI Werbestudio Website")}&body=${body}`;
    setSent(true);
    setTimeout(() => setSent(false), 4000);
  };

  return (
    <section id="kontakt" className="py-28 bg-card/50 border-t border-border" ref={ref}>
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={springGentle}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <span
            className="inline-block text-xs font-semibold uppercase tracking-[0.2em] mb-4 px-3 py-1 rounded-full border"
            style={{ color: "oklch(0.72 0.20 55)", borderColor: "oklch(0.72 0.20 55 / 0.3)", background: "oklch(0.72 0.20 55 / 0.08)" }}
          >
            Kontakt
          </span>
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-foreground mb-5">
            Lassen Sie uns{" "}
            <span style={{ color: "oklch(0.72 0.20 55)" }}>starten</span>
          </h2>
          <p className="text-muted-foreground text-lg leading-relaxed">
            Bereit für professionelle KI-Werbung und Social-Media-Kommunikation? Nehmen Sie jetzt Kontakt auf für eine persönliche Beratung.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 max-w-6xl mx-auto">
          {/* Contact info cards */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ ...springGentle, delay: 0.15 }}
            className="lg:col-span-2 space-y-4"
          >
            {[
              { icon: <Phone size={20} />, label: "Telefon", value: COMPANY.phone, href: `tel:${COMPANY.phone}` },
              { icon: <Mail size={20} />, label: "E-Mail", value: COMPANY.email, href: `mailto:${COMPANY.email}` },
              { icon: <MapPin size={20} />, label: "Adresse", value: COMPANY.address, href: undefined },
              { icon: <Clock size={20} />, label: "Öffnungszeiten", value: COMPANY.openingHours, href: undefined },
            ].map((item, i) => (
              <motion.div
                key={item.label}
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ ...springGentle, delay: 0.2 + i * 0.08 }}
                className="flex items-start gap-4 rounded-2xl bg-background border border-border p-5 hover:border-primary/40 transition-colors"
              >
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: "oklch(0.72 0.20 55 / 0.12)", color: "oklch(0.72 0.20 55)" }}
                >
                  {item.icon}
                </div>
                <div>
                  <div className="text-xs text-muted-foreground uppercase tracking-wide mb-1">{item.label}</div>
                  {item.href ? (
                    <a href={item.href} className="text-foreground text-sm hover:text-primary transition-colors font-medium">
                      {item.value}
                    </a>
                  ) : (
                    <div className="text-foreground text-sm font-medium">{item.value}</div>
                  )}
                </div>
              </motion.div>
            ))}

            {/* Facebook badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ ...springGentle, delay: 0.55 }}
              className="rounded-2xl bg-background border border-border p-5"
            >
              <div className="text-xs text-muted-foreground uppercase tracking-wide mb-3">Social Media</div>
              <a
                href="https://www.facebook.com/KittyDudacy"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 text-sm text-foreground hover:text-primary transition-colors"
              >
                <span className="text-xl">📘</span>
                <span className="font-medium">facebook.com/KittyDudacy</span>
              </a>
            </motion.div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ ...springGentle, delay: 0.2 }}
            className="lg:col-span-3"
          >
            <div
              className="rounded-3xl bg-background border border-border p-8 relative overflow-hidden"
              style={{ boxShadow: "0 20px 60px -15px oklch(0.72 0.20 55 / 0.15)" }}
            >
              <div
                className="absolute -bottom-20 -right-20 w-60 h-60 rounded-full opacity-5"
                style={{ background: "radial-gradient(circle, oklch(0.72 0.20 55) 0%, transparent 70%)" }}
              />

              <h3 className="font-heading font-bold text-foreground text-xl mb-6">Jetzt Anfrage senden</h3>

              {sent && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-center gap-2 text-sm mb-6 p-3 rounded-xl"
                  style={{ background: "oklch(0.72 0.20 55 / 0.1)", color: "oklch(0.72 0.20 55)" }}
                >
                  <CheckCircle2 size={16} />
                  Ihr E-Mail-Client wurde geöffnet. Wir freuen uns auf Ihre Nachricht!
                </motion.div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4 relative z-10">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-muted-foreground uppercase tracking-wide block mb-1.5">
                      Name *
                    </label>
                    <input
                      required
                      type="text"
                      value={form.name}
                      onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                      className="w-full px-4 py-3 rounded-xl bg-muted border border-border text-foreground text-sm placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/60 transition-colors"
                      placeholder="Max Mustermann"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground uppercase tracking-wide block mb-1.5">
                      E-Mail *
                    </label>
                    <input
                      required
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                      className="w-full px-4 py-3 rounded-xl bg-muted border border-border text-foreground text-sm placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/60 transition-colors"
                      placeholder="max@unternehmen.de"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-muted-foreground uppercase tracking-wide block mb-1.5">
                      Telefon
                    </label>
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                      className="w-full px-4 py-3 rounded-xl bg-muted border border-border text-foreground text-sm placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/60 transition-colors"
                      placeholder="015143120308"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground uppercase tracking-wide block mb-1.5">
                      Betreff
                    </label>
                    <select
                      value={form.subject}
                      onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
                      className="w-full px-4 py-3 rounded-xl bg-muted border border-border text-foreground text-sm focus:outline-none focus:border-primary/60 transition-colors"
                    >
                      <option value="">Bitte wählen…</option>
                      <option value="KI-Design Anfrage">KI-Design Anfrage</option>
                      <option value="Social Media Betreuung">Social Media Betreuung</option>
                      <option value="Komplettpaket KI + Social">Komplettpaket KI + Social</option>
                      <option value="Allgemeine Anfrage">Allgemeine Anfrage</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="text-xs text-muted-foreground uppercase tracking-wide block mb-1.5">
                    Ihre Nachricht *
                  </label>
                  <textarea
                    required
                    rows={5}
                    value={form.message}
                    onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                    className="w-full px-4 py-3 rounded-xl bg-muted border border-border text-foreground text-sm placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/60 transition-colors resize-none"
                    placeholder="Beschreiben Sie kurz Ihr Projekt oder Ihre Anfrage…"
                  />
                </div>

                <button
                  type="submit"
                  className="w-full px-6 py-4 bg-primary text-primary-foreground rounded-xl font-semibold text-base hover:bg-accent transition-all hover:scale-[1.02] glow-orange flex items-center justify-center gap-2"
                >
                  <Send size={16} />
                  Anfrage jetzt senden
                </button>
              </form>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
